import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Modal component for displaying images with navigation
 * @param {Object} props - Component props
 * @param {string} props.image - URL of the image to display
 * @param {string} [props.title] - Optional title for the image
 * @param {string} [props.description] - Optional description for the image
 * @param {Function} props.onClose - Function to call when modal is closed
 * @param {Function} props.onNext - Function to call when next button is clicked
 * @param {Function} props.onPrev - Function to call when previous button is clicked
 * @param {boolean} props.hasNext - Whether there is a next image available
 * @param {boolean} props.hasPrev - Whether there is a previous image available
 */
const ImageModal = ({ 
    image, 
    title, 
    description, 
    onClose, 
    onNext, 
    onPrev, 
    hasNext, 
    hasPrev 
}) => {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight' && hasNext) onNext();
            if (e.key === 'ArrowLeft' && hasPrev) onPrev();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose, onNext, onPrev, hasNext, hasPrev]);

    if (!image) return null;
    
    return (
        <div 
            role="dialog"
            aria-modal="true"
            aria-label="Image viewer"
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" 
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="relative max-w-4xl w-full h-[90vh] md:h-[80vh] flex items-center justify-center">
                <button 
                    onClick={onClose} 
                    className="absolute top-2 md:top-4 right-2 md:right-4 text-white text-4xl leading-none hover:text-gray-300 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center"
                    aria-label="Close modal"
                >
                    &times;
                </button>
                
                {hasPrev && (
                    <button 
                        onClick={(e) => { e.stopPropagation(); onPrev(); }}
                        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 text-white text-4xl leading-none hover:text-gray-300 bg-black bg-opacity-50 rounded-full w-14 h-14 md:w-12 md:h-12 flex items-center justify-center"
                        aria-label="Previous image"
                    >
                        &#8249;
                    </button>
                )}
                
                {hasNext && (
                    <button 
                        onClick={(e) => { e.stopPropagation(); onNext(); }}
                        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 text-white text-4xl leading-none hover:text-gray-300 bg-black bg-opacity-50 rounded-full w-14 h-14 md:w-12 md:h-12 flex items-center justify-center"
                        aria-label="Next image"
                    >
                        &#8250;
                    </button>
                )}
                
                <img 
                    src={image} 
                    alt={title || ''} 
                    role="img"
                    className="max-w-full max-h-[50vh] md:max-h-[60vh] object-contain" 
                />
                
                {title && description && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 p-4 md:p-6 text-white">
                        <h3 className="text-xl md:text-2xl font-semibold mb-2">{title}</h3>
                        <p className="text-sm md:text-base text-gray-300">{description}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

ImageModal.propTypes = {
    image: PropTypes.string.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onPrev: PropTypes.func.isRequired,
    hasNext: PropTypes.bool.isRequired,
    hasPrev: PropTypes.bool.isRequired
};

export default ImageModal;
